/* eslint-disable react-refresh/only-export-components */
import { useEffect, useState } from "react";
import type React from "react";
import toast from "react-hot-toast";
import {
  TerminalSettingsRepository,
  type TerminalBehaviorSettings,
} from "../../application/settings/terminal_settings_repository";
import { tipPresetCountWarning } from "./tip_presets";

const repo = new TerminalSettingsRepository();

// ── Pure helpers (exported for future unit tests) ─────────────────────────────

export interface TipPreset {
  type: "percent" | "fixed";
  value: number;
}

/** Parse presetsJson into an array of TipPreset. Returns [] on blank/invalid. */
export function parsePresets(presetsJson: string): TipPreset[] {
  if (!presetsJson.trim()) return [];
  try {
    const parsed = JSON.parse(presetsJson);
    if (!Array.isArray(parsed)) return [];
    return parsed as TipPreset[];
  } catch {
    return [];
  }
}

/**
 * Convert a comma-separated percent string (e.g. "15, 18, 20") into a JSON
 * presets array. Returns "" if the input is blank (repository stores '[]',
 * clearing any previously saved presets).
 * Throws a descriptive error if any value is non-numeric or negative.
 */
export function percentsToPresetsJson(input: string): string {
  const trimmed = input.trim();
  if (!trimmed) return "";
  const parts = trimmed.split(",").map((s) => s.trim());
  const presets: TipPreset[] = parts.map((p) => {
    const n = p === "" ? NaN : Number(p);
    if (!Number.isFinite(n))
      throw new Error(`Invalid tip preset value: "${p}"`);
    if (n < 0) throw new Error(`Tip preset cannot be negative: ${n}`);
    return { type: "percent", value: n };
  });
  return JSON.stringify(presets);
}

/** Render presets read-only, e.g. "$5, $10" for fixed or "15%, 18%" mixed. */
export function formatPresetsReadOnly(presets: TipPreset[]): string {
  return presets
    .map((p) => (p.type === "fixed" ? `$${p.value}` : `${p.value}%`))
    .join(", ");
}

// ── Small shared sub-components ───────────────────────────────────────────────

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="bg-card rounded-lg border p-5">
      <h3 className="text-foreground mb-4 text-base font-semibold">{title}</h3>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Toggle({
  label,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label className="flex items-center gap-2">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className={disabled ? "text-subtle-foreground" : undefined}>
        {label}
      </span>
    </label>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function TerminalSettingsTab({ businessId }: { businessId: string }) {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Invoice
  const [invoiceRequired, setInvoiceRequired] = useState(false);

  // Tip
  const [tipEnabled, setTipEnabled] = useState(false);
  const [tipPresetsInput, setTipPresetsInput] = useState("");
  const [presetsEditable, setPresetsEditable] = useState(true);
  const [originalPresetsJson, setOriginalPresetsJson] = useState("");
  const [tipAllowCustom, setTipAllowCustom] = useState(false);
  const [tipNoTip, setTipNoTip] = useState(true);

  // Tax
  const [taxEnabled, setTaxEnabled] = useState(false);
  const [taxPercentStr, setTaxPercentStr] = useState("0");
  const [taxLabel, setTaxLabel] = useState("");
  const [taxIncluded, setTaxIncluded] = useState(false);

  // Payments
  const [cashEnabled, setCashEnabled] = useState(true);

  // Receipt
  const [receiptHeader, setReceiptHeader] = useState("");
  const [receiptFooter, setReceiptFooter] = useState("");
  const [receiptShowLogo, setReceiptShowLogo] = useState(false);
  const [receiptAutoMerchant, setReceiptAutoMerchant] = useState(false);

  useEffect(() => {
    let active = true;
    setError(null);
    setLoadError(null);
    setTipPresetsInput("");
    setPresetsEditable(true);
    setOriginalPresetsJson("");

    (async () => {
      setLoading(true);
      try {
        const s: TerminalBehaviorSettings =
          await repo.getBehaviorSettings(businessId);
        if (!active) return;

        setInvoiceRequired(s.invoiceNumberRequired);

        setTipEnabled(s.tip.enabled);
        setTipAllowCustom(s.tip.allowCustom);
        setTipNoTip(s.tip.noTipEnabled);
        setOriginalPresetsJson(s.tip.presetsJson);
        const presets = parsePresets(s.tip.presetsJson);
        const allPercent =
          presets.length === 0 || presets.every((p) => p.type === "percent");
        setPresetsEditable(allPercent);
        setTipPresetsInput(
          allPercent
            ? presets.map((p) => String(p.value)).join(", ")
            : formatPresetsReadOnly(presets),
        );

        setTaxEnabled(s.tax.enabled);
        setTaxPercentStr(String(s.tax.percent));
        setTaxLabel(s.tax.label);
        setTaxIncluded(s.tax.included);

        setCashEnabled(s.cashEnabled);

        setReceiptHeader(s.receipt.header);
        setReceiptFooter(s.receipt.footer);
        setReceiptShowLogo(s.receipt.showLogo);
        setReceiptAutoMerchant(s.receipt.autoMerchantCopy);
      } catch (e) {
        if (active) setLoadError(e instanceof Error ? e.message : String(e));
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [businessId, reloadKey]);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      // Validate everything up-front so a bad field can't cause a partial save.
      const presetsJson = presetsEditable
        ? percentsToPresetsJson(tipPresetsInput)
        : originalPresetsJson;

      const trimmedTax = taxPercentStr.trim();
      const taxPercent = trimmedTax === "" ? 0 : Number(trimmedTax);
      if (!Number.isFinite(taxPercent) || taxPercent < 0) {
        throw new Error(`Invalid tax percent: "${taxPercentStr}"`);
      }

      await repo.saveInvoiceNumberRequired(businessId, invoiceRequired);
      await repo.saveTipSettings(businessId, {
        enabled: tipEnabled,
        presetsJson,
        allowCustom: tipAllowCustom,
        noTipEnabled: tipNoTip,
      });
      await repo.saveTaxSettings(businessId, {
        enabled: taxEnabled,
        percent: taxPercent,
        label: taxLabel,
        included: taxIncluded,
      });
      await repo.saveCashEnabled(businessId, cashEnabled);
      await repo.saveReceiptSettings(businessId, {
        header: receiptHeader,
        footer: receiptFooter,
        showLogo: receiptShowLogo,
        autoMerchantCopy: receiptAutoMerchant,
      });
      const presetWarning = tipPresetCountWarning(parsePresets(presetsJson), {
        tipEnabled,
      });
      toast.success("Settings saved");
      if (presetWarning) toast(presetWarning, { icon: "\u26a0\ufe0f" });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  if (loading)
    return <div className="text-muted-foreground p-4 text-sm">Loading…</div>;

  if (loadError) {
    return (
      <div className="space-y-3 p-4">
        <div className="text-sm text-red-600">
          Failed to load terminal settings: {loadError}
        </div>
        <button
          className="text-foreground hover:bg-muted rounded border px-4 py-2 text-sm"
          onClick={() => setReloadKey((k) => k + 1)}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4">
      <p className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
        Changes reach the terminal at its next settings sync (login or every 30
        minutes). If the merchant changed a setting on the terminal more
        recently, the terminal value wins.
      </p>

      <fieldset disabled={saving} className="space-y-6">
        <Section title="Invoice">
          <Toggle
            label="Require invoice # on every charge"
            checked={invoiceRequired}
            onChange={setInvoiceRequired}
          />
        </Section>

        <Section title="Tip">
          <Toggle
            label="Tips enabled"
            checked={tipEnabled}
            onChange={setTipEnabled}
          />
          <label className="block">
            <span className="text-muted-foreground block text-sm">
              Presets (comma-separated %, 3 for customer-display terminals)
            </span>
            {presetsEditable ? (
              <input
                className="mt-1 rounded border px-2 py-1"
                value={tipPresetsInput}
                placeholder="e.g. 15, 18, 20"
                onChange={(e) => setTipPresetsInput(e.target.value)}
              />
            ) : (
              <>
                <input
                  className="text-subtle-foreground mt-1 rounded border px-2 py-1"
                  value={tipPresetsInput}
                  disabled
                />
                <p className="text-muted-foreground mt-1 text-xs">
                  Fixed-amount presets are set on the terminal and can&apos;t be
                  edited here.
                </p>
              </>
            )}
          </label>
          <Toggle
            label="Allow custom tip"
            checked={tipAllowCustom}
            onChange={setTipAllowCustom}
          />
          <Toggle
            label='Show "No Tip" button'
            checked={tipNoTip}
            onChange={setTipNoTip}
          />
        </Section>

        <Section title="Tax">
          <Toggle
            label="Tax enabled"
            checked={taxEnabled}
            onChange={setTaxEnabled}
          />
          <div className="flex gap-4">
            <label className="block">
              <span className="text-muted-foreground block text-sm">
                Percent
              </span>
              <input
                className="mt-1 w-24 rounded border px-2 py-1"
                inputMode="decimal"
                value={taxPercentStr}
                onChange={(e) => setTaxPercentStr(e.target.value)}
              />
            </label>
            <label className="block">
              <span className="text-muted-foreground block text-sm">
                Label (on receipt)
              </span>
              <input
                className="mt-1 rounded border px-2 py-1"
                value={taxLabel}
                onChange={(e) => setTaxLabel(e.target.value)}
              />
            </label>
          </div>
          <Toggle
            label="Tax included in prices"
            checked={taxIncluded}
            onChange={setTaxIncluded}
          />
        </Section>

        <Section title="Payments">
          <Toggle
            label="Accept cash"
            checked={cashEnabled}
            onChange={setCashEnabled}
          />
        </Section>

        <Section title="Receipt">
          <label className="block">
            <span className="text-muted-foreground block text-sm">Header</span>
            <input
              className="mt-1 w-96 rounded border px-2 py-1"
              value={receiptHeader}
              onChange={(e) => setReceiptHeader(e.target.value)}
            />
          </label>
          <label className="block">
            <span className="text-muted-foreground block text-sm">Footer</span>
            <input
              className="mt-1 w-96 rounded border px-2 py-1"
              value={receiptFooter}
              onChange={(e) => setReceiptFooter(e.target.value)}
            />
          </label>
          <Toggle
            label="Show logo"
            checked={receiptShowLogo}
            onChange={setReceiptShowLogo}
          />
          <Toggle
            label="Auto-print merchant copy"
            checked={receiptAutoMerchant}
            onChange={setReceiptAutoMerchant}
          />
        </Section>

        {error && <div className="text-sm text-red-600">{error}</div>}

        <button
          className="rounded bg-blue-600 px-4 py-2 text-white disabled:opacity-50"
          disabled={saving}
          onClick={save}
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </fieldset>
    </div>
  );
}
